import { z } from 'zod';

// Matches the CMS field limit, with room for the current nationwide mapping.
export const MAXIMUM_DATA_CHARACTERS = 250_000;
export const POSTCODE_PATTERN = /^[0-9]{5}$/;

export enum ContactRole {
    Doctor = 'Doctor',
    DeputyDoctor = 'Deputy doctor',
    StudyNurse = 'Study nurse',
    StudyCoordinator = 'Study coordinator'
}

export enum PhoneLabel {
    Telephone = 'Telephone',
    Mobile = 'Mobile'
}

const text = z.string().trim().min(1).max(1000);
const identifier = z.string().regex(/^[0-9]{2}$/);
const phoneSchema = z.strictObject({
    label: z.enum(PhoneLabel),
    number: z.string().regex(/^\+[1-9][0-9]{6,14}$/)
});
const contactSchema = z.strictObject({
    name: text,
    role: z.enum(ContactRole),
    isMainContact: z.boolean(),
    phoneNumbers: z.array(phoneSchema).max(10),
    emails: z.array(z.email().max(254)).max(10)
});
const practiceSchema = z
    .strictObject({
        id: identifier,
        name: text,
        address: z.strictObject({ street: text, postcode: z.string().regex(POSTCODE_PATTERN), city: text }),
        contacts: z.array(contactSchema).min(1).max(30)
    })
    .refine((practice) => practice.contacts.some((contact) => contact.phoneNumbers.length || contact.emails.length));
const mappingSchema = z
    .custom<Record<string, unknown>>(
        (value) =>
            typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value) &&
            Object.keys(value).length > 0 &&
            Object.keys(value).length <= 12_000
    )
    .pipe(z.record(z.string().regex(POSTCODE_PATTERN), identifier));

const contactDataSchema = z.strictObject({
    schemaVersion: z.literal(1),
    practices: z.array(practiceSchema).min(1).max(100)
});

export const postcodeFinderSchema = z
    .strictObject({
        contactData: contactDataSchema,
        postcodeMapping: mappingSchema
    })
    .refine(({ contactData, postcodeMapping }) => {
        const identifiers = new Set(contactData.practices.map((practice) => practice.id));
        return (
            identifiers.size === contactData.practices.length &&
            Object.values(postcodeMapping).every((reference) => identifiers.has(reference))
        );
    });

export type FinderContact = z.infer<typeof contactSchema>;
export type FinderPractice = z.infer<typeof practiceSchema>;

export function parsePostcodeFinderData(contactSource: unknown, mappingSource: unknown) {
    if (
        typeof contactSource !== 'string' ||
        typeof mappingSource !== 'string' ||
        contactSource.length > MAXIMUM_DATA_CHARACTERS ||
        mappingSource.length > MAXIMUM_DATA_CHARACTERS
    )
        return null;
    try {
        const result = postcodeFinderSchema.safeParse({
            contactData: JSON.parse(contactSource),
            postcodeMapping: JSON.parse(mappingSource)
        });
        if (!result.success) return null;
        return {
            ...result.data.contactData,
            postcodeToPractice: result.data.postcodeMapping,
            practicesById: new Map(result.data.contactData.practices.map((practice) => [practice.id, practice]))
        };
    } catch {
        return null;
    }
}

export const contactRoleKeys: Record<ContactRole, string> = {
    [ContactRole.Doctor]: 'postcodeFinder.doctor',
    [ContactRole.DeputyDoctor]: 'postcodeFinder.deputyDoctor',
    [ContactRole.StudyNurse]: 'postcodeFinder.studyNurse',
    [ContactRole.StudyCoordinator]: 'postcodeFinder.studyCoordinator'
};

export const phoneLabelKeys: Record<PhoneLabel, string> = {
    [PhoneLabel.Telephone]: 'postcodeFinder.telephone',
    [PhoneLabel.Mobile]: 'postcodeFinder.mobile'
};
